import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import ApiService from '../services/ApiService';
import '../styles/components.css';

// Theme-aware styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--bg-card)',
    borderColor: state.isFocused ? 'var(--accent)' : 'var(--border)',
    color: 'var(--text-primary)',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(57,122,158,0.15)' : 'none',
    fontSize: 13,
    '&:hover': { borderColor: 'var(--accent)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--accent)' : state.isFocused ? 'rgba(57,122,158,0.1)' : 'transparent',
    color: state.isSelected ? '#fff' : 'var(--text-primary)',
    fontSize: 13,
    '&:hover': { backgroundColor: state.isSelected ? 'var(--accent)' : 'rgba(57,122,158,0.1)' }
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  multiValue: (provided) => ({ ...provided, backgroundColor: 'rgba(57,122,158,0.15)', borderRadius: 6 }),
  multiValueLabel: (provided) => ({ ...provided, color: 'var(--text-primary)', fontSize: 12 }),
  multiValueRemove: (provided) => ({
    ...provided, color: 'var(--text-secondary)',
    '&:hover': { backgroundColor: 'var(--negative)', color: '#fff' }
  }),
  input: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-muted)', fontSize: 13 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 1020 })
};

const btnStyle = {
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s',
};

const btnPrimaryStyle = {
  ...btnStyle,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: '#fff',
};

const AdminTenantCountries = ({ tenantId, tenantName, onClose }) => {
  const [countries, setCountries] = useState([]);
  const [tenantCountries, setTenantCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMarket, setEditingMarket] = useState(null);
  const [marketForm, setMarketForm] = useState({
    name: '',
    country_ids: [],
    is_primary: false
  });

  useEffect(() => {
    fetchInitialData();
  }, [tenantId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [countriesResponse, tenantCountriesResponse] = await Promise.all([
        ApiService.getCountries(),
        ApiService.getTenantCountries(tenantId)
      ]);
      setCountries(countriesResponse.data);
      setTenantCountries(tenantCountriesResponse.data);
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMarket = () => {
    setEditingMarket(null);
    setMarketForm({ name: '', country_ids: [], is_primary: false });
    setIsModalOpen(true);
    setError('');
  };

  const handleEditMarket = (market) => {
    setEditingMarket(market);
    setMarketForm({
      name: market.name,
      country_ids: market.countries.map(c => c.id),
      is_primary: market.countries.some(c => c.pivot?.is_primary)
    });
    setIsModalOpen(true);
    setError('');
  };

  const handleSaveMarket = () => {
    if (!marketForm.name.trim()) {
      setError('Please enter a market name');
      return;
    }
    if (marketForm.country_ids.length === 0) {
      setError('Please select at least one country');
      return;
    }

    if (!editingMarket || editingMarket.name.toLowerCase() !== marketForm.name.toLowerCase()) {
      const markets = getMarketsFromCountries();
      if (markets.find(m => m.name.toLowerCase() === marketForm.name.toLowerCase())) {
        setError('A market with this name already exists');
        return;
      }
    }

    let updatedCountries = [...tenantCountries];

    if (editingMarket) {
      updatedCountries = updatedCountries.filter(c => c.pivot.market_name !== editingMarket.name);
    }

    if (marketForm.is_primary) {
      updatedCountries.forEach(c => { if (c.pivot) c.pivot.is_primary = false; });
    }

    marketForm.country_ids.forEach((countryId, index) => {
      const existingIndex = updatedCountries.findIndex(c => c.id === countryId);
      if (existingIndex !== -1) {
        updatedCountries[existingIndex].pivot.market_name = marketForm.name;
        updatedCountries[existingIndex].pivot.is_primary = marketForm.is_primary && index === 0;
      } else {
        const countryDetails = countries.find(c => c.id === countryId);
        if (countryDetails) {
          updatedCountries.push({
            ...countryDetails,
            pivot: { market_name: marketForm.name, is_primary: marketForm.is_primary && index === 0 }
          });
        }
      }
    });

    setTenantCountries(updatedCountries);
    setIsModalOpen(false);
    setError('');
    setSuccess(editingMarket ? 'Market updated successfully!' : 'Market created successfully!');
  };

  const handleDeleteMarket = (market) => {
    if (window.confirm(`Are you sure you want to delete the market "${market.name}"?`)) {
      setTenantCountries(tenantCountries.filter(c => c.pivot.market_name !== market.name));
      setSuccess('Market deleted successfully!');
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const countriesData = tenantCountries.map(c => ({
        country_id: c.id,
        market_name: c.pivot?.market_name || null,
        is_primary: c.pivot?.is_primary || false
      }));
      await ApiService.updateTenantCountries(tenantId, countriesData);
      setSuccess('Tenant countries updated successfully!');
      await fetchInitialData();
    } catch (err) {
      setError('Failed to update: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getMarketsFromCountries = () => {
    const marketMap = {};
    tenantCountries.forEach(country => {
      const marketName = country.pivot.market_name;
      if (marketName) {
        if (!marketMap[marketName]) {
          marketMap[marketName] = { name: marketName, countries: [], isPrimary: false };
        }
        marketMap[marketName].countries.push(country);
        if (country.pivot.is_primary) marketMap[marketName].isPrimary = true;
      }
    });
    return Object.values(marketMap);
  };

  const getUnassignedCountries = () => {
    return tenantCountries.filter(c => !c.pivot.market_name);
  };

  const markets = getMarketsFromCountries();
  const unassigned = getUnassignedCountries();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={btnPrimaryStyle} onClick={handleAddMarket} disabled={loading}>
          <i className="fas fa-plus" style={{ marginRight: 6 }} />
          Add Market
        </button>
      </div>

      {error && <div className="fc-callout fc-callout-error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="fc-callout fc-callout-success" style={{ marginBottom: 12 }}>{success}</div>}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0' }}>
          <div className="fc-spinner" />
          <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>Loading countries...</div>
        </div>
      ) : (
        <>
          {markets.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Markets</div>
              <table className="fc-table">
                <thead>
                  <tr>
                    <th>Market Name</th>
                    <th>Countries</th>
                    <th>Primary</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((market, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{market.name}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {market.countries.map(country => (
                            <span key={country.id} className="fc-badge fc-badge-gray">
                              {country.emoji} {country.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {market.isPrimary ? (
                          <span className="fc-badge fc-badge-green">
                            <i className="fas fa-star" style={{ marginRight: 4, fontSize: 10 }} />
                            Primary
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button style={btnStyle} onClick={() => handleEditMarket(market)}>Edit</button>
                          <button
                            style={{ ...btnStyle, color: 'var(--negative)', borderColor: 'rgba(224,108,117,0.3)' }}
                            onClick={() => handleDeleteMarket(market)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {unassigned.length > 0 && (
            <div className="fc-callout fc-callout-info" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 13 }}>Unassigned countries:</span>
                {unassigned.map(country => (
                  <span key={country.id} className="fc-badge fc-badge-gold">{country.emoji} {country.name}</span>
                ))}
              </div>
            </div>
          )}

          {tenantCountries.length === 0 && (
            <div className="fc-callout fc-callout-info" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <i className="fas fa-globe" style={{ fontSize: 24, color: 'var(--text-muted)', marginBottom: 12, display: 'block' }} />
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No markets configured yet</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Create your first market by clicking "Add Market" above.
              </div>
            </div>
          )}

          {tenantCountries.length > 0 && markets.length === 0 && (
            <div className="fc-callout fc-callout-info" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Countries need to be organized into markets</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                You have countries assigned but they're not organized into markets yet.
              </div>
              <button style={btnPrimaryStyle} onClick={handleAddMarket}>Create First Market</button>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              style={{ ...btnPrimaryStyle, opacity: saving || tenantCountries.length === 0 ? 0.5 : 1 }}
              onClick={handleSaveChanges}
              disabled={saving || tenantCountries.length === 0}
            >
              {saving && <div className="fc-spinner" style={{ width: 14, height: 14, marginRight: 8, borderWidth: 2 }} />}
              Save Changes
            </button>
          </div>
        </>
      )}

      {/* Add/Edit Market Modal — portalled to body so it's not clipped by outer modal */}
      {createPortal(
        <div className={`modal-overlay${isModalOpen ? ' open' : ''}`} onClick={() => setIsModalOpen(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <div className="modal-panel-header">
              <div className="modal-panel-title">
                {editingMarket ? `Edit Market: ${editingMarket.name}` : `Add New Market to ${tenantName}`}
              </div>
              <button className="modal-panel-close" onClick={() => setIsModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-panel-body">
              {error && <div className="fc-callout fc-callout-error" style={{ marginBottom: 12 }}>{error}</div>}

              <div className="modal-field">
                <label>Market Name *</label>
                <input
                  type="text"
                  value={marketForm.name}
                  onChange={(e) => setMarketForm({ ...marketForm, name: e.target.value })}
                  placeholder="e.g., Nordic Region, DACH Region"
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Enter a descriptive name for this market
                </div>
              </div>

              <div className="modal-field">
                <label>Countries *</label>
                <Select
                  isMulti
                  value={marketForm.country_ids.map(id => {
                    const country = countries.find(c => c.id === id);
                    return country ? { value: country.id, label: `${country.emoji} ${country.name}` } : null;
                  }).filter(Boolean)}
                  onChange={(opts) => setMarketForm({ ...marketForm, country_ids: opts ? opts.map(o => o.value) : [] })}
                  options={countries
                    .filter(country => {
                      if (editingMarket) {
                        return !tenantCountries.some(tc => tc.id === country.id && tc.pivot.market_name !== editingMarket.name);
                      }
                      return !tenantCountries.some(tc => tc.id === country.id);
                    })
                    .map(c => ({ value: c.id, label: `${c.emoji} ${c.name}` }))
                  }
                  placeholder="Select countries for this market..."
                  closeMenuOnSelect={false}
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Select one or more countries that belong to this market
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={marketForm.is_primary}
                    onChange={(e) => setMarketForm({ ...marketForm, is_primary: e.target.checked })}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  Set as primary market
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, paddingLeft: 24 }}>
                  Only one market can be primary per tenant.
                </div>
              </div>
            </div>
            <div className="modal-panel-footer">
              <button className="modal-btn ghost" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="modal-btn primary" onClick={handleSaveMarket}>
                {editingMarket ? 'Update Market' : 'Create Market'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminTenantCountries;
